import { Box, Shadow, Stack, VStack } from '@genial/components';
import { BoxRadius } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import React, { useEffect, useMemo } from 'react';
import { FlatList } from 'react-native';

import { useScrollCloseToBottomContext } from '../../../../contexts/ScrollCloseToBottom';
import { CustomerV4FinancialResponseNormalized } from '../../../../store/customer/types';
import { CustomerListBottom } from '../CustomerListBottom';
import { CustomerListEmpty } from '../CustomerListEmpty';
import { CustomerListLoadingMemoized } from '../CustomerListLoading';
import { CustomerTableItem } from '../CustomerTableItem';
import { TableHead } from '../TableHead';

import { customersFlatListProps } from './constants';

export interface CustomerListProps {
  customers: CustomerV4FinancialResponseNormalized[];
  isLoading: boolean;
  isLoadingMore: boolean;
  onEndReached: () => void;
  onSortingChanged: () => void;
  currentPage: number;
  hasFilter?: boolean;
  hasNextPage?: boolean;
}

export const CustomerList: React.FC<CustomerListProps> = ({
  customers,
  isLoading,
  isLoadingMore,
  onEndReached,
  onSortingChanged,
  currentPage,
  hasFilter,
  hasNextPage = false,
}) => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isDesktop = useBreakpointValue({ base: false, sm: false, lg: true });

  const { isCloseToBottom } = useScrollCloseToBottomContext();

  const flatListMemoized = useMemo(() => {
    const flatList = (
      <FlatList
        data={customers}
        {...customersFlatListProps}
        maxToRenderPerBatch={20}
        updateCellsBatchingPeriod={5}
        keyExtractor={(item) => item.customerId.toString()}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <CustomerListEmpty
            isLoading={isLoading}
            hasFilter={!!hasFilter}
            hasData={!!customers.length}
          />
        }
        onEndReached={!isMobile ? onEndReached : undefined}
        onEndReachedThreshold={currentPage === 1 ? 0.15 : 0.5}
        ListFooterComponent={
          <CustomerListBottom
            dataLength={customers.length}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
          />
        }
        ItemSeparatorComponent={() => <Box h="stack.nano" />}
        renderItem={({ item: customer }) => {
          return <CustomerTableItem customer={customer} />;
        }}
      />
    );

    if (isMobile) {
      return flatList;
    }

    return (
      <Shadow level={2} borderTopRadius={BoxRadius.sm}>
        <VStack maxH="700px" overflowY="auto">
          {flatList}
        </VStack>
      </Shadow>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, isLoading, isLoadingMore, isMobile]);

  const loadingInitialProducts = isLoading && currentPage === 1;

  const renderPrincipal = () => {
    if (loadingInitialProducts) {
      return <CustomerListLoadingMemoized quantity={7} />;
    }

    return flatListMemoized;
  };

  useEffect(() => {
    if (isCloseToBottom && !isLoading && isMobile) {
      onEndReached();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloseToBottom, isLoading, isMobile]);

  return (
    <VStack>
      {!!isDesktop && <TableHead orderCycleMode="full" onSortingChanged={onSortingChanged} />}
      <Stack mx={isMobile ? undefined : 'inline.xxxs'}>{renderPrincipal()}</Stack>
    </VStack>
  );
};
